import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useOrgContext } from '../../entities/org';
import { CreateWorkspaceButton } from '../../features/workspace/create-workspace';
import { Badge } from '../../shared/ui/badge';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../shared/ui/card';
import { EmptyState } from '../../shared/ui/empty-state';
import { SettingsPage } from './settings-page';

/**
 * `/o/:orgSlug/settings/workspaces`: every workspace the caller can see, one row each. A row opens that
 * workspace's settings where the caller manages it, and its projects otherwise.
 */
export function OrgWorkspacesPage() {
  const { t } = useTranslation('org');
  const { org, can, canIn, activeWorkspaces, archivedWorkspaces } = useOrgContext();
  const workspaces = [...activeWorkspaces, ...archivedWorkspaces];
  return (
    <SettingsPage section="workspaces">
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('settings.workspaces.title')}</h2>
          </CardTitle>
          <CardDescription>{t('settings.workspaces.help')}</CardDescription>
          {can('workspaces.manage') ? (
            <CardAction>
              <CreateWorkspaceButton org={org} />
            </CardAction>
          ) : null}
        </CardHeader>
        <CardContent>
          {workspaces.length === 0 ? (
            <EmptyState size="compact" title={t('settings.workspaces.empty')} />
          ) : (
            <ul aria-label={t('settings.workspaces.title')} className="rounded-sm border">
              {workspaces.map((workspace) => {
                const manages = canIn(workspace.id, 'workspace.manage');
                const archived = workspace.archived_at !== null;
                return (
                  <li key={workspace.id} className="border-b last:border-b-0">
                    <Link
                      to={
                        manages
                          ? `/o/${org.slug}/settings/workspaces/${workspace.slug}`
                          : `/o/${org.slug}/w/${workspace.slug}`
                      }
                      aria-label={t(
                        manages
                          ? 'settings.workspaces.settingsOf'
                          : 'settings.workspaces.projectsOf',
                        { name: workspace.name },
                      )}
                      className="flex items-center gap-3 px-4 py-3 text-foreground no-underline transition-colors duration-150 ease-standard hover:bg-muted hover:text-foreground"
                    >
                      <span className="min-w-0 flex-1 truncate font-bold text-heading">
                        {workspace.name}
                      </span>
                      <Badge variant={archived ? 'outline' : 'secondary'}>
                        {archived
                          ? t('settings.workspaces.archived')
                          : t('settings.workspaces.active')}
                      </Badge>
                      <ChevronRightIcon
                        aria-hidden="true"
                        className="size-4 shrink-0 text-muted-foreground"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </SettingsPage>
  );
}
